import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useT } from "../../lib/i18n/useT";

// ── Icons ──────────────────────────────────────────────────────────────────

function DashboardIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}
function PeopleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/>
    </svg>
  );
}
function CustomersIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 17V11a2 2 0 0 1 4 0v2"/>
      <path d="M4 13l-2-4"/>
      <path d="M20 17V11a2 2 0 0 0-4 0v2"/>
      <path d="M20 13l2-4"/>
      <path d="M8 13h8"/>
      <path d="M4 17l-2 3"/>
      <path d="M20 17l2 3"/>
    </svg>
  );
}
function MatrixIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
    </svg>
  );
}
function VacanciesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
    </svg>
  );
}
function StackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
  );
}
function DocsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  );
}
function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: collapsed ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }}>
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

// ── NavItem ────────────────────────────────────────────────────────────────

function NavItem({
  label, tooltip, icon, isActive, collapsed, onClick,
}: {
  label: string; tooltip: string; icon: React.ReactNode;
  isActive: boolean; collapsed: boolean; onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <div className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        onClick={onClick}
        title={collapsed ? tooltip : undefined}
        className={`flex flex-col items-center gap-1.5 rounded-lg transition-all duration-150 cursor-pointer focus:outline-none w-full py-2.5 ${
          collapsed ? "px-0" : "px-1"
        } ${
          isActive
            ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900"
            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-slate-100"
        }`}
      >
        <span className="shrink-0">{icon}</span>
        {!collapsed && (
          <span className="text-[10px] font-medium leading-tight text-center w-full truncate">
            {label}
          </span>
        )}
      </button>

      {collapsed && hover && (
        <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50">
          <div className="rounded-md bg-slate-900 dark:bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-white dark:text-slate-900 whitespace-nowrap shadow-lg">
            {tooltip}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900 dark:border-r-slate-100" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "lens_sidebar_collapsed";

// Nav path → its own path plus any children that should keep the item highlighted.
function isActivePath(itemPath: string, currentPath: string): boolean {
  if (itemPath === "/") return currentPath === "/";
  return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
}

export default function Sidebar() {
  const t = useT();
  const [pathname, navigate] = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "true");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const navItems: { path: string; label: string; tooltip: string; icon: React.ReactNode }[] = [
    { path: "/",            label: t("nav.dashboard"), tooltip: "Start",         icon: <DashboardIcon /> },
    { path: "/consultants", label: t("nav.people"),    tooltip: "People",        icon: <PeopleIcon /> },
    { path: "/customers",   label: t("nav.customers"), tooltip: "Customers",     icon: <CustomersIcon /> },
    { path: "/matrix",      label: t("nav.matrix"),    tooltip: "Match Matrix",  icon: <MatrixIcon /> },
    { path: "/vacancies",   label: t("nav.vacancies_short"), tooltip: "Vacancies", icon: <VacanciesIcon /> },
    { path: "/stack",       label: "Stack",            tooltip: "Stack",         icon: <StackIcon /> },
    { path: "/docs",        label: "Docs",             tooltip: "Documentation", icon: <DocsIcon /> },
  ];

  return (
    <aside
      className={`h-full border-r border-slate-200/80 dark:border-slate-700/50 bg-canvas dark:bg-[#0e1520] flex flex-col shrink-0 transition-all duration-200 ${
        collapsed ? "w-14" : "w-24"
      }`}
    >
      <nav className={`flex flex-col gap-0.5 flex-1 pt-4 ${collapsed ? "px-2" : "px-3"}`}>
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            label={item.label}
            tooltip={item.tooltip}
            icon={item.icon}
            isActive={isActivePath(item.path, pathname)}
            collapsed={collapsed}
            onClick={() => navigate(item.path)}
          />
        ))}
      </nav>

      <div className={`pb-4 ${collapsed ? "px-2" : "px-3"}`}>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className={`flex items-center gap-3 rounded-lg px-3 py-2 w-full transition-colors cursor-pointer focus:outline-none text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 ${
            collapsed ? "justify-center px-0" : ""
          }`}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <CollapseIcon collapsed={collapsed} />
          {!collapsed && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
